import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
  Input,
  DatePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Partner } from '../../model/Partner.model';
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

interface PartnerListTableProps {
  partners: Partner[];
  loading: boolean;
  onEdit: (partner: Partner) => void;
  onDelete: (id: number) => void;
}

const PartnerListTable: React.FC<PartnerListTableProps> = ({
  partners,
  loading,
  onEdit,
  onDelete,
}) => {
  // State for search values
  const [partnerCodeSearch, setPartnerCodeSearch] = useState<string>('');
  const [nameSearch, setNameSearch] = useState<string>('');
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [phoneSearch, setPhoneSearch] = useState<string>('');
  const [dateRangeFilter, setDateRangeFilter] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null]);
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>(partners);

  // Update filtered data when source data or filters change
  useEffect(() => {
    const filtered = partners.filter((partner) => {
      // Apply all active filters

      if (
        nameSearch &&
        !partner.name.toLowerCase().includes(nameSearch.toLowerCase())
      ) {
        return false;
      }

      if (
        emailSearch &&
        !partner.email?.toLowerCase().includes(emailSearch.toLowerCase())
      ) {
        return false;
      }

      if (
        phoneSearch &&
        !partner.phone?.toLowerCase().includes(phoneSearch.toLowerCase())
      ) {
        return false;
      }

      // Date range filter
      if (
        dateRangeFilter[0] &&
        dateRangeFilter[1] &&
        partner.registrationDate
      ) {
        const partnerDate = dayjs(partner.registrationDate);
        if (
          !partnerDate.isAfter(dateRangeFilter[0]) ||
          !partnerDate.isBefore(dateRangeFilter[1].add(1, 'day'))
        ) {
          return false;
        }
      }

      return true;
    });

    setFilteredPartners(filtered);
  }, [
    partners,
    partnerCodeSearch,
    nameSearch,
    emailSearch,
    phoneSearch,
    dateRangeFilter,
  ]);

  // Handle search input changes
  const handleSearchInput = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setter(value);
  };

  const columns: ColumnsType<Partner> = [
    {
      title: 'Tên đối tác',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm theo tên đối tác"
            value={nameSearch}
            onChange={(e) => handleSearchInput(e.target.value, setNameSearch)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm theo email"
            value={emailSearch}
            onChange={(e) => handleSearchInput(e.target.value, setEmailSearch)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm theo số điện thoại"
            value={phoneSearch}
            onChange={(e) => handleSearchInput(e.target.value, setPhoneSearch)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Ngày đăng ký',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={[dateRangeFilter[0], dateRangeFilter[1]]}
            onChange={(dates) => {
              setDateRangeFilter(dates as [dayjs.Dayjs, dayjs.Dayjs]);
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },

    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>
          {status === 'ACTIVE' ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      ),
      filters: [
        {
          text: 'Hoạt động',
          value: 'ACTIVE',
        },
        {
          text: 'Không hoạt động',
          value: 'INACTIVE',
        },
      ],
      onFilter: (value: any, record: Partner) => record.status === value,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size={10}>
          <Tooltip title="Chỉnh sửa">
            <Button
              type="default"
              icon={<EditOutlined style={{ fontSize: '18px' }} />}
              onClick={() => onEdit(record)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              className="w-8 h-8"
              variant="link"
              color="blue"
            />
          </Tooltip>
          <Popconfirm
            title="Xóa đối tác"
            description="Bạn có chắc chắn muốn xóa đối tác này?"
            onConfirm={() => onDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa đối tác">
              <Button
                type="default"
                icon={<DeleteOutlined style={{ fontSize: '18px' }} />}
                color="red"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                className="w-8 h-8"
                variant="link"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={filteredPartners}
      loading={loading}
      rowKey="id"
      scroll={{ x: 1500 }}
      className="w-full"
      pagination={{
        pageSize: 10,
        showTotal: (total) => `Tổng số ${total} đối tác`,
        showSizeChanger: false,
      }}
    />
  );
};

export default PartnerListTable;
